import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaRegClock,
  FaUsers,
  FaTrophy,
  FaCheck,
  FaUserPlus,
  FaArrowRight,
  FaInfoCircle,
} from "react-icons/fa";
import { HiSparkles } from "react-icons/hi2";
import { BsLightningFill } from "react-icons/bs";

const PackageCards = ({ dynamicPackages = [] }) => {
  const navigate = useNavigate();
  const [playerCount, setPlayerCount] = useState(12);
  const [manualSelectedPkg, setManualSelectedPkg] = useState(null);

  // Automatically determine the recommended package based on the slider value
  const getAutoMatchId = (count) => {
    if (count <= 10) return "small-squad";
    if (count <= 15) return "all-star";
    return "big-league";
  };

  const currentMatchId = manualSelectedPkg || getAutoMatchId(playerCount);

  const packages = [
    {
      id: "small-squad",
      title: "SMALL SQUAD",
      badge: null,
      capacityPill: "Up to 10 players",
      subtitle: "Ideal for intimate birthdays & close friend matchups",
      price: "$275",
      perParticipant: "Just ~$27.50 per participant",
      features: [
        {
          icon: <FaRegClock />,
          title: "90 Minutes of Sports",
          desc: "Structured game time with arena equipment",
        },
        {
          icon: <FaUsers />,
          title: "45–60 Minutes Party / Social Area",
          desc: "Dedicated private bay for cake, food & awards",
        },
        {
          icon: <FaTrophy />,
          title: (
            <span>
              Sport Choice:{" "}
              <strong className="text-white">Soccer included</strong>
            </span>
          ),
          desc: "Soccer pitch included; sport upgrades available",
        },
        {
          icon: <FaCheck />,
          title: "Tables & Chairs",
          desc: "Reserved seating arranged for all guests",
        },
        {
          icon: <HiSparkles />,
          title: "Setup & Cleanup Included",
          desc: "Strikes & Shots crew handles before & after",
        },
        {
          icon: <FaUserPlus />,
          title: (
            <span>
              Additional Participants:{" "}
              <strong className="text-primaryBtnHoverColor">$20 / person</strong>
            </span>
          ),
          desc: "Up to 12 participants with add-ons",
        },
      ],
    },
    {
      id: "all-star",
      title: "ALL-STAR SQUAD",
      badge: {
        icon: <HiSparkles className="text-xs" />,
        text: "YOUR BEST MATCH",
        isHighlight: true,
      },
      capacityPill: "Up to 15 players",
      subtitle: "Our most popular choice for team parties & birthdays",
      price: "$375",
      perParticipant: "Just ~$25.00 per participant",
      features: [
        {
          icon: <FaRegClock />,
          title: "90 Minutes of Sports",
          desc: "Structured game time with arena equipment",
        },
        {
          icon: <FaUsers />,
          title: "45–60 Minutes Party / Social Area",
          desc: "Dedicated private bay for cake, food & awards",
        },
        {
          icon: <FaTrophy />,
          title: (
            <span>
              Sport Choice:{" "}
              <strong className="text-white">
                Choice of 1 Sport (Soccer, Basketball, Volleyball, Cricket)
              </strong>
            </span>
          ),
          desc: "Choose 1: Soccer, Basketball, Volleyball, or Cricket",
        },
        {
          icon: <FaCheck />,
          title: "Tables & Chairs",
          desc: "Reserved seating arranged for all guests",
        },
        {
          icon: <HiSparkles />,
          title: "Setup & Cleanup Included",
          desc: "Strikes & Shots crew handles before & after",
        },
        {
          icon: <FaUserPlus />,
          title: (
            <span>
              Additional Participants:{" "}
              <strong className="text-primaryBtnHoverColor">$25 / person</strong>
            </span>
          ),
          desc: "Up to 18 participants with add-ons",
        },
      ],
    },
    {
      id: "big-league",
      title: "BIG LEAGUE",
      badge: {
        icon: <BsLightningFill className="text-xs" />,
        text: "ULTIMATE VALUE",
        isHighlight: false,
      },
      capacityPill: "Up to 20 players",
      subtitle: "Maximum squad energy for milestone bashes & large groups",
      price: "$475",
      perParticipant: "Just ~$23.75 per participant",
      features: [
        {
          icon: <FaRegClock />,
          title: "90 Minutes of Sports",
          desc: "Structured game time with arena equipment",
        },
        {
          icon: <FaUsers />,
          title: "45–60 Minutes Party / Social Area",
          desc: "Dedicated private bay for cake, food & awards",
        },
        {
          icon: <FaTrophy />,
          title: (
            <span>
              Sport Choice:{" "}
              <strong className="text-white">
                Choice of 1 Sport (Soccer, Basketball, Volleyball, Cricket)
              </strong>
            </span>
          ),
          desc: "Choose 1: Soccer, Basketball, Volleyball, or Cricket",
        },
        {
          icon: <FaCheck />,
          title: "Tables & Chairs",
          desc: "Reserved seating arranged for all guests",
        },
        {
          icon: <HiSparkles />,
          title: "Setup & Cleanup Included",
          desc: "Strikes & Shots crew handles before & after",
        },
        {
          icon: <FaUserPlus />,
          title: (
            <span>
              Additional Participants:{" "}
              <strong className="text-primaryBtnHoverColor">$30 / person</strong>
            </span>
          ),
          desc: "25+ participants: Custom pricing",
        },
      ],
    },
  ];

  const displayPackages = useMemo(() => {
    // When dynamic packages are loaded from backend API
    if (dynamicPackages && Array.isArray(dynamicPackages)) {
      if (dynamicPackages.length === 0) return [];

      return dynamicPackages
        .filter((d) => d.isActive !== false)
        .map((dyn) => {
          const matchedStatic = packages.find(
            (p) =>
              p.id === dyn.slug ||
              (dyn.slug && (dyn.slug === "all-star-squad" && p.id === "all-star")) ||
              (dyn.slug && dyn.slug.includes(p.id)) ||
              (dyn.name && dyn.name.toLowerCase().includes(p.id.replace("-", " ")))
          );

          let features = matchedStatic ? matchedStatic.features : [];
          if (dyn.features && dyn.features.length > 0) {
            features = dyn.features.map((featStr) => {
              let icon = <FaCheck />;
              const lower = String(featStr).toLowerCase();
              if (lower.includes("minute") || lower.includes("time")) {
                icon = <FaRegClock />;
              } else if (lower.includes("bay") || lower.includes("area") || lower.includes("room") || lower.includes("social")) {
                icon = <FaUsers />;
              } else if (lower.includes("sport") || lower.includes("soccer") || lower.includes("court") || lower.includes("pitch")) {
                icon = <FaTrophy />;
              } else if (lower.includes("clean") || lower.includes("setup")) {
                icon = <HiSparkles />;
              } else if (lower.includes("participant") || lower.includes("extra") || lower.includes("player")) {
                icon = <FaUserPlus />;
              }
              return {
                icon,
                title: featStr,
                desc: "",
              };
            });
          }

          return {
            id: dyn.slug || (matchedStatic ? matchedStatic.id : dyn._id),
            title: dyn.name ? dyn.name.toUpperCase().replace(" PARTY", "") : (matchedStatic ? matchedStatic.title : "SQUAD PARTY"),
            badge: dyn.badge
              ? { icon: <HiSparkles className="text-xs" />, text: dyn.badge, isHighlight: dyn.isPopular }
              : dyn.isPopular
              ? { icon: <HiSparkles className="text-xs" />, text: "YOUR BEST MATCH", isHighlight: true }
              : null,
            capacityPill: dyn.partySize || (dyn.baseParticipants ? `Up to ${dyn.baseParticipants} players` : (matchedStatic ? matchedStatic.capacityPill : "")),
            subtitle: matchedStatic ? matchedStatic.subtitle : "Exclusive sports party experience",
            price: dyn.price != null ? `$${dyn.price}` : (matchedStatic ? matchedStatic.price : "$275"),
            perParticipant: dyn.baseParticipants
              ? `Just ~$${Math.round(dyn.price / dyn.baseParticipants)}.00 per participant`
              : (matchedStatic ? matchedStatic.perParticipant : ""),
            features,
          };
        });
    }

    // Default while API call is resolving
    return packages;
  }, [dynamicPackages]);

  const getMatchedTitle = () => {
    const matched = displayPackages.find((p) => p.id === currentMatchId);
    if (matched) return matched.title;
    if (displayPackages.length > 0) return displayPackages[0].title;
    return "SQUAD PARTY";
  };

  const handleSelect = (pkgTitle) => {
    navigate("/party-request", {
      state: { selectedPackage: pkgTitle, players: playerCount },
    });
  };

  return (
    <div
      id="packages"
      className="w-full py-20 px-4 md:px-8 lg:px-12 bg-[#050733] select-none text-white relative"
    >
      <div className="max-w-7xl mx-auto flex flex-col items-center">
        {/* Section Heading */}
        <h2 className="text-3xl sm:text-5xl md:text-6xl font-heading font-black text-white uppercase text-center tracking-tight leading-tight">
          CHOOSE YOUR SQUAD PACKAGE
        </h2>

        {/* Subtitle */}
        <p className="mt-4 text-gray-300 font-subheading text-base sm:text-lg max-w-3xl text-center leading-relaxed">
          Three simple tiers built around active group size. Pure playing time,
          dedicated party suites, and zero hidden venue fees.
        </p>

        {/* Interactive Squad Size Slider Box */}
        <div className="w-full max-w-xl mx-auto mt-10 p-5 sm:p-7 rounded-2xl bg-[#080b2c]/90 border border-white/10 shadow-2xl backdrop-blur-sm">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2.5 text-gray-200 font-subheading font-medium text-sm sm:text-base">
              <FaUsers className="text-primaryBtnHoverColor text-lg" />
              <span>How many participating players?</span>
            </div>
            <div className="bg-primaryBtnHoverColor text-white font-heading font-bold text-sm sm:text-base px-3.5 py-1 rounded-md shadow-md">
              {playerCount} Players
            </div>
          </div>

          {/* Slider input */}
          <div className="mt-6">
            <input
              type="range"
              min="6"
              max="25"
              step="1"
              value={playerCount}
              onChange={(e) => {
                setPlayerCount(parseInt(e.target.value, 10));
                setManualSelectedPkg(null);
              }}
              className="w-full h-2.5 bg-slate-700/80 rounded-lg appearance-none cursor-pointer accent-[#CD1B1B] focus:outline-none"
            />

            {/* Slider Milestones */}
            <div className="flex justify-between items-center text-xs text-gray-400 font-subheading mt-2.5 px-0.5">
              <span>6 (Intimate)</span>
              <span>10 (Small)</span>
              <span>16 (All-Star)</span>
              <span>20+ (Big League)</span>
            </div>
          </div>

          {/* Dynamic Match Text */}
          <div className="mt-4 pt-3.5 border-t border-white/10 text-center text-xs sm:text-sm font-subheading text-gray-300">
            Recommended match for {playerCount} players:{" "}
            <span className="font-bold text-primaryBtnHoverColor uppercase font-heading">
              {getMatchedTitle()}
            </span>
          </div>
        </div>

        {/* 3 Package Cards Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch w-full mt-14">
          {displayPackages.map((pkg) => {
            const isSelected = currentMatchId === pkg.id;

            return (
              <div
                key={pkg.id}
                onClick={() => setManualSelectedPkg(pkg.id)}
                className={`relative rounded-2xl p-6 sm:p-8 flex flex-col justify-between transition-all duration-300 cursor-pointer ${
                  isSelected
                    ? "bg-[#0a0e36] border-2 border-primaryBtnHoverColor shadow-[0_0_35px_rgba(205,27,27,0.28)] lg:-translate-y-2"
                    : "bg-[#080b28]/90 border border-white/10 hover:border-white/20 hover:bg-[#0a0d33]"
                }`}
              >
                {/* Top Badge (if present or if active match) */}
                {isSelected ? (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-primaryBtnHoverColor text-white text-[11px] font-heading font-bold uppercase tracking-wider px-4 py-1 rounded-full shadow-md flex items-center gap-1.5 border border-red-400">
                    <HiSparkles className="text-xs" />
                    <span>YOUR BEST MATCH</span>
                  </div>
                ) : pkg.badge ? (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-[#172048] text-gray-200 text-[11px] font-heading font-bold uppercase tracking-wider px-4 py-1 rounded-full shadow-md flex items-center gap-1.5 border border-white/20">
                    {pkg.badge.icon}
                    <span>{pkg.badge.text}</span>
                  </div>
                ) : null}

                <div>
                  {/* Card Header: Title + Capacity Pill */}
                  <div className="flex items-center justify-between gap-2 mt-1">
                    <h3 className="text-xl sm:text-2xl font-heading font-extrabold text-white uppercase tracking-wide">
                      {pkg.title}
                    </h3>
                    <span className="bg-slate-800/80 border border-slate-700 text-gray-300 text-xs font-subheading font-medium px-2.5 py-1 rounded-md">
                      {pkg.capacityPill}
                    </span>
                  </div>

                  {/* Subtitle */}
                  <p className="text-gray-400 font-subheading text-xs sm:text-sm mt-2 leading-relaxed min-h-[38px]">
                    {pkg.subtitle}
                  </p>

                  {/* Price Block */}
                  <div className="mt-5 pb-6 border-b border-white/10">
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-4xl sm:text-5xl font-heading font-black text-white">
                        {pkg.price}
                      </span>
                      <span className="text-gray-400 font-subheading text-sm font-medium">
                        / package
                      </span>
                    </div>

                    <div className="flex items-center justify-between text-xs mt-2.5">
                      <span className="text-gray-400 font-subheading">
                        {pkg.perParticipant}
                      </span>
                      <div className="text-right">
                        <span className="text-[11px] text-gray-400 block font-subheading">
                          Spectators
                        </span>
                        <span className="text-emerald-400 font-heading font-bold text-xs tracking-wider uppercase">
                          ALWAYS FREE
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Features List */}
                  <div className="space-y-4 my-6">
                    {pkg.features.map((item, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <span className="text-primaryBtnHoverColor text-sm mt-1 flex-shrink-0">
                          {item.icon}
                        </span>
                        <div>
                          <div className="text-white font-subheading text-sm font-semibold leading-snug">
                            {item.title}
                          </div>
                          {item.desc && (
                            <div className="text-gray-400 font-subheading text-xs mt-0.5 leading-snug">
                              {item.desc}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Bottom CTA Button */}
                <div className="pt-4 mt-auto">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelect(pkg.title);
                    }}
                    className={`w-full py-3.5 px-4 rounded-lg font-heading font-bold text-sm sm:text-base transition-all duration-200 flex items-center justify-center gap-2 uppercase tracking-wider cursor-pointer ${
                      isSelected
                        ? "bg-primaryBtnHoverColor hover:bg-red-800 text-white shadow-lg shadow-red-950/40 hover:scale-[1.02] active:scale-95"
                        : "bg-white/10 hover:bg-white hover:text-primaryBtnColor text-white border border-white/20 active:scale-95"
                    }`}
                  >
                    <span>SELECT PACKAGE</span>
                    <FaArrowRight className="text-xs" />
                  </button>

                  <div className="flex items-center justify-center gap-1.5 text-gray-400 text-[11px] font-subheading mt-3 text-center">
                    <FaInfoCircle className="text-[10px]" />
                    <span>Hold your slot with no immediate charge</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PackageCards;
